import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthHeader } from 'components';
import SignUpEmail from 'components/SignUp/SignUpEmail';
import { appConfig } from 'config/app';
import { InviteService } from 'lib';
import validations from 'lib/Validations';
import { URL } from 'routes';
import { SignUpEmailVerification } from 'screens/SignUp';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectInvite, verifyInviteRequest } from 'store/Invites';
import { createSignUpUserRequest, selectSignUpUser, SignUp } from 'store/SignUp';
import { IStore } from 'store/types';
import { ICreateUserRequest, IdentityType } from 'types';
import { getIdentityByType } from 'utils/identity';

const SignUpCreateAccountEmail: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.CREATE_SIGN_UP_USER_REQUEST));
  const signUpUser = useSelector(selectSignUpUser());
  const signUpErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, SignUp.CREATE_SIGN_UP_USER_REQUEST));
  const invite = useSelector(selectInvite);
  const inviteToken = InviteService.getInviteToken();

  const validateField = (field: 'firstName' | 'lastName' | 'identity' | 'password' | 'termsOfService' | 'privacyPolicy') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, setFieldValue, getFieldProps, handleBlur, resetForm } = useFormik({
    initialValues: {
      firstName: signUpUser?.firstName || '',
      lastName: signUpUser?.lastName || '',
      identity: signUpUser ? getIdentityByType(signUpUser.identities, IdentityType.EMAIL)?.value : '',
      password: '',
      termsOfService: false,
      privacyPolicy: false,
    },
    onSubmit: () => {
      const payload = {
        ...values,
        termsOfService: {
          version: appConfig.VERSIONS.TERMS_OF_SERVICE,
        },
        privacyPolicy: {
          version: appConfig.VERSIONS.PRIVACY_POLICY,
        },
      } as ICreateUserRequest;
      dispatch(createSignUpUserRequest(payload, { onSuccess }));
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape(validations.signUpValidationSchema),
  });

  const onSuccess = () => {
    setShowEmailVerification(true);
  };

  const onBack = () => {
    setShowEmailVerification(false);
  };

  useEffect(() => {
    if (!invite && inviteToken) dispatch(verifyInviteRequest(inviteToken));
  }, [invite, inviteToken, dispatch]);

  useEffect(() => {
    resetForm();
    dispatch(clearRequest(SignUp.CREATE_SIGN_UP_USER_REQUEST));
    dispatch(clearRequest(SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
  }, [showEmailVerification, dispatch, resetForm]);

  return showEmailVerification && signUpUser ? (
    <SignUpEmailVerification onBack={onBack} signUpUser={signUpUser} />
  ) : (
    <SignUpEmail
      authHeader={
        <AuthHeader
          data-cy="sign-up-header"
          title="Sign Up With Email"
          subtitle="Fill out the following to create an account."
          onBack={() => {
            resetForm();
            history.replace(!invite ? URL.SignUp : `/invites/${invite.id}`);
          }}
        />
      }
      onSubmit={handleSubmit}
      validateField={validateField}
      setFieldValue={setFieldValue}
      getFieldProps={getFieldProps}
      isValid={isValid}
      dirty={dirty}
      values={values}
      errorMessages={signUpErrors?.errorMessages}
      formId={SignUp.CREATE_SIGN_UP_USER_REQUEST}
      errors={errors}
      isLoading={isLoading}
      handleBlur={handleBlur}
    />
  );
};

export default SignUpCreateAccountEmail;
